import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isPlatformScoped } from '../utils/permissions';
dayjs.extend(utc);
import { apiFetch } from '../api';
import { useData } from '../hooks/useData';
import { useAuth } from '../context/useAuth';
import { LoadingSpinner } from '../components/Common';
import { PageBanner } from '../components/PageBanner';
import DashboardViewPage from './dashboard/DashboardViewPage';
import FlowRunViewer from '../components/flows/FlowRunViewer';
import FlowEditorPage from './FlowEditorPage';
// Phase 2: Connect Your Data
import ConnectDataPanel from '../components/TenantPortal/ConnectDataPanel';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    LayoutDashboard, Play, ChevronLeft, X,
    CheckCircle2, XCircle, Loader2, Clock, AlertCircle,
    History, Terminal, SkipForward, Layers, Zap,
    BarChart3, RefreshCw, Database, Timer,
    TrendingUp, Activity, CalendarClock, Hash,
    Rocket, Users, Workflow, ChevronRight, UserCheck, GitBranch,
    ArrowLeft, Maximize, Minimize, GitCommitHorizontal, Eye, Settings, ChevronDown,
} from 'lucide-react';
import {
    DropdownMenu as RunnerDropdownMenu,
    DropdownMenuContent as RunnerDropdownMenuContent,
    DropdownMenuItem as RunnerDropdownMenuItem,
    DropdownMenuTrigger as RunnerDropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { useNotification } from '../context/NotificationContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const statusColor = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'completed': case 'success': case 'done':
            return { bg: 'bg-emerald-500', faint: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' };
        case 'failed': case 'error':
            return { bg: 'bg-red-500', faint: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' };
        case 'running': case 'in_progress':
            return { bg: 'bg-primary', faint: 'bg-primary/10 dark:bg-primary/20', text: 'text-primary dark:text-primary', border: 'border-primary/20 dark:border-primary/30' };
        case 'waiting_approval': case 'awaiting_approval': case 'waiting':
            return { bg: 'bg-amber-500', faint: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' };
        case 'queued': case 'pending':
            return { bg: 'bg-slate-400', faint: 'bg-slate-50 dark:bg-slate-800/30', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-200 dark:border-slate-700' };
        default:
            return { bg: 'bg-gray-300', faint: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-500', border: 'border-gray-200 dark:border-gray-700' };
    }
};

const StatusDot = ({ status }) => {
    const c = statusColor(status);
    const isRunning = ['running', 'in_progress'].includes((status || '').toLowerCase());
    return (
        <span className="relative flex items-center justify-center w-4 h-4">
            {isRunning && <span className={`absolute inline-flex h-full w-full rounded-full ${c.bg} opacity-30 animate-ping`} />}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${c.bg}`} />
        </span>
    );
};

const StatusIcon = ({ status, size = 4 }) => {
    const cls = `w-${size} h-${size}`;
    switch ((status || '').toLowerCase()) {
        case 'completed': case 'success': case 'done':
            return <CheckCircle2 className={`${cls} text-emerald-500`} />;
        case 'failed': case 'error':
            return <XCircle className={`${cls} text-red-500`} />;
        case 'running': case 'in_progress':
            return <Loader2 className={`${cls} text-primary animate-spin`} />;
        case 'waiting_approval': case 'awaiting_approval': case 'waiting':
            return <UserCheck className={`${cls} text-amber-500`} />;
        case 'skipped':
            return <SkipForward className={`${cls} text-gray-400`} />;
        case 'queued': case 'pending':
            return <Clock className={`${cls} text-slate-400`} />;
        default:
            return <div className={`${cls} rounded-full border-2 border-gray-200 dark:border-gray-600`} />;
    }
};

const fmtRows = (n) => n != null ? Number(n).toLocaleString() : null;
const fmtDuration = (ms) => {
    if (ms == null) return null;
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
};
const fmtRelative = (dateStr, formatFn, fallback) => {
    if (!dateStr) return fallback ?? '—';
    return (formatFn ? formatFn(dateStr) : dayjs(dateStr).fromNow()) ?? fallback ?? '—';
};


// ─── Step Node Type Badge ─────────────────────────────────────────────────────

const NodeTypeBadge = ({ nodeType }) => {
    const t = (nodeType || '').toUpperCase();
    if (['HUMAN_INTERVENTION', 'APPROVAL', 'HUMAN_TASK'].some(k => t.includes(k))) {
        return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded"><UserCheck className="w-2.5 h-2.5" />Approval</span>;
    }
    if (['CONDITION', 'BRANCH', 'DECISION', 'ROUTER'].some(k => t.includes(k))) {
        return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-1.5 py-0.5 rounded"><GitBranch className="w-2.5 h-2.5" />Branch</span>;
    }
    if (['DATASOURCE', 'DATABASE', 'QUERY'].some(k => t.includes(k))) {
        return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded"><Database className="w-2.5 h-2.5" />Query</span>;
    }
    return null;
};

const ExecutionConsole = ({ onClose, onRun, isExecuting, flowSteps, stepStatus, currentRun, error, onApprove, linkedDashboardId, onOpenDashboard }) => {
    const navigate = useNavigate();
    // ── Overall run timer ────────────────────────────────────────────────────
    const [elapsed, setElapsed] = useState(0);
    const timerRef = useRef(null);
    // ── Per-step timers: track elapsed seconds for each running step ─────────
    const [stepElapsed, setStepElapsed] = useState({});
    const stepStartRef = useRef({});

    // Overall elapsed timer — starts/stops with isExecuting
    useEffect(() => {
        if (isExecuting) {
            const start = dayjs();
            timerRef.current = setInterval(() => setElapsed(dayjs().diff(start, 'second')), 1000);
        } else {
            clearInterval(timerRef.current);
            if (!isExecuting) setElapsed(0);
        }
        return () => clearInterval(timerRef.current);
    }, [isExecuting]);

    // Per-step elapsed: whenever stepStatus changes, detect newly running steps
    // and tick seconds for them independently of the overall timer.
    useEffect(() => {
        const now = dayjs();
        // Register start times for steps that just became running
        flowSteps.forEach(s => {
            const st = (stepStatus[s.id]?.status || '').toLowerCase();
            if (['running', 'in_progress'].includes(st) && !stepStartRef.current[s.id]) {
                stepStartRef.current[s.id] = now;
            }
            // Clear start time when step finishes
            if (!['running', 'in_progress'].includes(st) && st !== '' && stepStartRef.current[s.id]) {
                delete stepStartRef.current[s.id];
            }
        });

        // Tick all registered step timers every second
        const stepTimer = setInterval(() => {
            const updated = {};
            Object.entries(stepStartRef.current).forEach(([id, start]) => {
                updated[id] = dayjs().diff(start, 'second');
            });
            setStepElapsed(updated);
        }, 1000);
        return () => clearInterval(stepTimer);
    }, [stepStatus, flowSteps]);

    const approvalStep = flowSteps.find(s => ['waiting_approval', 'awaiting_approval', 'waiting'].includes((stepStatus[s.id]?.status || '').toLowerCase()));

    const fmtElapsed = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
    };

    return (
        <div className="flex flex-col h-full bg-white border-l border-gray-200 text-gray-900">
            {/* Header */}
            <div className="h-13 flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-gray-900 rounded-md flex items-center justify-center">
                        <Terminal className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-sm font-bold text-gray-900">Execution Console</span>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded">
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Run Button — shows overall run time */}
            <div className="px-4 py-4 border-b border-gray-100 shrink-0">
                <button
                    onClick={onRun}
                    disabled={isExecuting || !!approvalStep}
                    className={`w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-bold tracking-wide transition-all active:scale-[0.98] ${isExecuting || approvalStep
                        ? 'bg-primary/50 text-white cursor-not-allowed'
                        : 'bg-primary hover:opacity-90 text-white shadow-lg shadow-primary/20'
                        }`}>
                    {isExecuting
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Running… {fmtElapsed(elapsed)}</>
                        : <><Play className="w-4 h-4" fill="currentColor" /> Run Application</>}
                </button>
                {error && <p className="mt-2 text-xs text-red-500 text-center">{error}</p>}
                
                {/* Dashboard Link - Show when completed and linked */}
                {!isExecuting && !!linkedDashboardId && ['Completed', 'Success', 'COMPLETED', 'SUCCESS'].includes(currentRun?.status) && (
                    <button
                        onClick={() => onOpenDashboard(linkedDashboardId)}
                        className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-primary/20 bg-primary/5 [hover]:bg-primary/10 text-primary font-medium text-sm transition-colors"
                    >
                        <LayoutDashboard className="w-4 h-4" />
                        View Results Dashboard
                    </button>
                )}
            </div>

            {/* Approval banner */}
            {approvalStep && (
                <div className="mx-4 mt-4 p-3.5 rounded-xl border border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-2 mb-2">
                        <UserCheck className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="text-sm font-bold text-amber-700">Awaiting Approval</span>
                    </div>
                    <p className="text-xs text-amber-600 mb-3"><strong>{approvalStep.name}</strong> requires human review.</p>
                    <div className="flex gap-2">
                        <button onClick={() => onApprove?.(approvalStep.id, 'approve')}
                            className="flex-1 py-1.5 px-3 text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors">✓ Approve</button>
                        <button onClick={() => onApprove?.(approvalStep.id, 'reject')}
                            className="flex-1 py-1.5 px-3 text-xs font-bold bg-white border border-amber-300 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">✕ Reject</button>
                    </div>
                </div>
            )}

            {/* ── Timeline View ── always top-to-bottom in flow order, scrollable ── */}
            <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
                {flowSteps.length === 0 ? (
                    <div className="py-12 text-center">
                        <Layers className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                        <p className="text-xs text-gray-400">Hit Run to see live progress</p>
                    </div>
                ) : (
                    <>
                        <p className="text-sm font-bold text-gray-800 mb-3">Live Progress</p>
                        {/* Timeline: each step in stable flowSteps order */}
                        <div className="relative">
                            {/* Vertical connector line */}
                            <div className="absolute left-[11px] top-4 bottom-4 w-px bg-gray-200" />
                            <div className="space-y-2">
                                {flowSteps.map((s, idx) => {
                                    const so = stepStatus[s.id] || {};
                                    const st = (so.status || '').toLowerCase();
                                    const isRunning = ['running', 'in_progress'].includes(st);
                                    const isDone = ['completed', 'success', 'done'].includes(st);
                                    const isFail = ['failed', 'error'].includes(st);
                                    const isApproval = ['waiting_approval', 'awaiting_approval', 'waiting'].includes(st);
                                    const isSkipped = st === 'skipped';
                                    const isPending = !st || ['idle', 'pending', 'queued'].includes(st);
                                    const stepDur = so.duration != null ? fmtDuration(so.duration) : null;
                                    const myStepElapsed = stepElapsed[s.id];

                                    // Card colour
                                    let cardCls = 'border border-gray-200 bg-gray-50 opacity-40'; // pending fallback
                                    if (isRunning) cardCls = 'border-2 border-primary/30 bg-primary/10';
                                    else if (isDone) cardCls = 'border border-emerald-200 bg-emerald-50';
                                    else if (isFail) cardCls = 'border border-red-200 bg-red-50';
                                    else if (isApproval) cardCls = 'border border-amber-200 bg-amber-50';
                                    else if (isSkipped) cardCls = 'border border-gray-200 bg-gray-50 opacity-60';

                                    return (
                                        <div key={s.id} className="flex items-start gap-2.5">
                                            {/* Timeline dot */}
                                            <div className="shrink-0 mt-3.5 z-10">
                                                <StatusIcon status={st || 'idle'} size={4} />
                                            </div>
                                            {/* Card */}
                                            <div className={`flex-1 rounded-xl p-3 ${cardCls}`}>
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className={`text-sm font-semibold ${isPending ? 'text-gray-400' : 'text-gray-800'
                                                        }`}>{s.name}</p>
                                                    {/* Per-step elapsed (only while running, independent of overall timer) */}
                                                    {isRunning && myStepElapsed != null && (
                                                        <span className="text-[11px] font-mono text-primary shrink-0">
                                                            {fmtElapsed(myStepElapsed)}
                                                        </span>
                                                    )}
                                                    {/* Completed duration */}
                                                    {(isDone || isFail) && stepDur && (
                                                        <span className={`text-[11px] font-mono shrink-0 ${isFail ? 'text-red-400' : 'text-emerald-600'
                                                            }`}>{stepDur}</span>
                                                    )}
                                                </div>

                                                {/* Sub-text */}
                                                {isRunning && (
                                                    <>
                                                        <p className="text-xs text-primary mt-0.5">
                                                            {so.rows_affected != null ? `${fmtRows(so.rows_affected)} rows processed` : 'Processing…'}
                                                        </p>
                                                        <div className="mt-2 h-1 rounded-full bg-primary/10 overflow-hidden">
                                                            <div className="h-full bg-primary rounded-full"
                                                                style={{ width: '60%', animation: 'pulse 1.2s ease-in-out infinite' }} />
                                                        </div>
                                                    </>
                                                )}
                                                {isDone && (
                                                    <p className="text-xs text-emerald-600 mt-0.5">
                                                        {so.rows_affected != null
                                                            ? `${fmtRows(so.rows_affected)} rows processed`
                                                            : 'Completed'}
                                                    </p>
                                                )}
                                                {isFail && (
                                                    <p className="text-xs text-red-500 mt-0.5">{so.error_message || 'Failed'}</p>
                                                )}
                                                {isApproval && (
                                                    <p className="text-xs text-amber-600 mt-0.5">Awaiting approval</p>
                                                )}
                                                {<NodeTypeBadge nodeType={s.type} />}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Footer */}
            <div className="shrink-0 px-4 py-2 border-t border-gray-100 flex justify-between text-[10px] text-gray-400">
                <span className="font-mono">{currentRun ? `#${currentRun.id?.slice(-8).toUpperCase()}` : 'READY'}</span>
                <span>{flowSteps.length > 0 ? `${flowSteps.length} nodes` : ''}</span>
            </div>
        </div>
    );
};

// ─── Section Icon ─────────────────────────────────────────────────────────────


const sectionMeta = {
    'assigned_apps': { icon: Users, color: 'text-violet-500', bg: 'bg-violet-500', faint: 'bg-violet-50 dark:bg-violet-900/20', label: 'Assigned Apps', desc: 'Apps assigned to your account', gradient: 'from-violet-500 to-purple-600' },
    'dashboards': { icon: BarChart3, color: 'text-blue-500', bg: 'bg-blue-500', faint: 'bg-blue-50 dark:bg-blue-900/20', label: 'Dashboards', desc: 'Published dashboard views', gradient: 'from-blue-500 to-cyan-500' },
    'projects': { icon: Workflow, color: 'text-emerald-500', bg: 'bg-emerald-500', faint: 'bg-emerald-50 dark:bg-emerald-900/20', label: 'Projects', desc: 'Your flow projects', gradient: 'from-emerald-500 to-teal-600' },
    'apps': { icon: Rocket, color: 'text-indigo-500', bg: 'bg-indigo-500', faint: 'bg-indigo-50 dark:bg-indigo-900/20', label: 'Apps', desc: 'Published applications', gradient: 'from-indigo-500 to-violet-600' },
};

// ─── Content/App Card — Gallery ───────────────────────────────────────────────

// Shared helper: returns true for any item that should be treated as a dashboard,
// whether its type is the canonical 'dashboard' or the legacy 'content' +
// content_type='dashboard' that exists in bundles created before Fix A.
const isDashboardItem = (item) =>
    item?.type === 'dashboard' ||
    (item?.type === 'content' && (item?.content_type === 'dashboard' || (item?.contentTypeName || '').toLowerCase() === 'dashboard'));

const ItemCard = ({ item, sectionKey, onClick, formatDate, formatRelativeTime, onConfigData, isAdmin }) => {
    const meta = sectionMeta[sectionKey] || sectionMeta.apps;
    const isDash = item.type === 'dashboard' || item.content_type === 'dashboard' || sectionKey === 'dashboards';
    const Icon = isDash ? BarChart3 : Zap;
    const iconBg = isDash ? 'bg-primary' : 'bg-primary';

    return (
        <div className="group relative w-full text-left flex flex-col rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 transition-all duration-200 hover:border-indigo-200 dark:hover:border-indigo-800 hover:shadow-xl hover:-translate-y-1 focus-within:outline-none">
            <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl ${iconBg} text-white flex items-center justify-center shadow-sm`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1.5">
                    {/* Per-card data config button — admin only */}
                    {isAdmin && onConfigData && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onConfigData(item); }}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors opacity-0 group-hover:opacity-100"
                            title="Configure data connection for this item"
                        >
                            <Settings className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-gray-50 dark:bg-gray-800 text-gray-400">
                        {isDash ? 'Dashboard' : 'Flow'}
                    </span>
                </div>
            </div>
            <button onClick={onClick} className="text-left flex-1 flex flex-col focus:outline-none">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-1">{item.name}</h3>
                <p className="text-xs text-gray-400 line-clamp-2 flex-1 mb-4">{item.description || (isDash ? 'View data insights' : 'Execute this flow')}</p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-50 dark:border-gray-800">
                    <span className="text-xs text-gray-300 dark:text-gray-600">
                        {item.lastRun ? fmtRelative(item.lastRun, formatRelativeTime) : 'Never run'}
                    </span>
                    <span className={`text-xs font-bold ${isDash ? 'text-blue-500' : 'text-indigo-500'} transition-transform group-hover:translate-x-0.5`}>
                        {isDash ? 'Open →' : '▶ Run →'}
                    </span>
                </div>
            </button>
        </div>
    );
};

// ─── Run History Row ──────────────────────────────────────────────────────────

const RunHistoryRow = ({ run, onClick, isSelected, onViewResults, formatDate }) => {
    const dur = run.completed_at && run.started_at
        ? dayjs(run.completed_at).diff(dayjs(run.started_at)) : null;
    const c = statusColor(run.status);
    const statusLower = (run.status || '').toLowerCase();
    const isFailed = ['failed', 'error'].includes(statusLower);
    const isRunning = ['running', 'in_progress'].includes(statusLower);

    return (
        <tr onClick={onClick}
            className={`cursor-pointer transition-colors ${isSelected ? 'bg-primary/10' : 'hover:bg-gray-50'
                }`}>
            {/* STATUS DOT */}
            <td className="pl-5 pr-2 py-3.5 w-10">
                <StatusDot status={run.status} />
            </td>
            {/* DATE / TIME */}
            <td className="pr-4 py-3.5">
                <p className="text-sm font-semibold text-gray-800">
                    {formatDate(run.started_at)}
                </p>
                {isFailed && run.error_message && (
                    <p className="text-[11px] text-red-500 truncate max-w-[260px] mt-0.5" title={run.error_message}>
                        ✕ {run.error_message}
                    </p>
                )}
            </td>
            {/* DURATION */}
            <td className="pr-4 py-3.5 text-sm text-gray-500 font-mono">
                {dur != null ? fmtDuration(dur) : <span className="text-gray-300">—</span>}
            </td>
            {/* ROWS PROCESSED */}
            <td className="pr-4 py-3.5">
                {run.rows_affected != null ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                        <Database className="w-3 h-3" />
                        {fmtRows(run.rows_affected)} rows
                    </span>
                ) : (
                    <span className="text-gray-300 text-sm">—</span>
                )}
            </td>
            {/* STATUS PILL */}
            <td className="pr-2 py-3.5">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border ${c.faint} ${c.text} ${c.border}`}>
                    <StatusIcon status={run.status} size={3} />
                    {isRunning ? 'Running' : run.status}
                </span>
            </td>
            {/* VIEW RESULTS */}
            <td className="pr-5 py-3.5 text-right">
                {['completed', 'success', 'done'].includes(statusLower) && onViewResults && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onViewResults(run); }}
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-primary hover:opacity-80 bg-primary/10 hover:bg-primary/20 border border-primary/20 px-2.5 py-1 rounded-full transition-colors"
                    >
                        <LayoutDashboard className="w-3 h-3" />
                        View Results
                    </button>
                )}
            </td>
        </tr>
    );
};

// ─── Run Detail Panel ─────────────────────────────────────────────────────────


// ─── Page Header ─────────────────────────────────────────────────────────────

const PageHeader = ({ crumbs, rightActions }) => (
    <div className="shrink-0 h-14 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex items-center px-5 gap-3 z-20">
        {crumbs.map((c, i) => (
            <React.Fragment key={i}>
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
                {c.onClick ? (
                    <button onClick={c.onClick} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                        {i === 0 && <ChevronLeft className="w-4 h-4" />}
                        <span>{c.label}</span>
                    </button>
                ) : (
                    <span className={`text-sm font-bold ${i === crumbs.length - 1 ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500'}`}>{c.label}</span>
                )}
                {c.badge && (
                    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary/10 text-primary dark:bg-opacity-20`}>
                        {c.badge}
                    </span>
                )}
            </React.Fragment>
        ))}
        {rightActions && <div className="ml-auto flex items-center gap-2">{rightActions}</div>}
    </div>
);

// ─── TenantAppRunnerPage ──────────────────────────────────────────────────────
/**
 * Multi-level My Apps navigator.
 *
 * Props from PlatformLayout:
 *   navMode    - 'master' | 'section' | 'app'
 *   sectionKey - 'assigned_apps' | 'dashboards' | 'projects' | 'apps'
 *   sectionTitle - display name for the section
 *   sectionItems - raw items array for section gallery
 *   appUid     - specific app UID
 *   contentUid - specific content UID (optional deep link)
 *   allSections - { assigned_apps:[], dashboards:[], projects:[], apps:[] }
 */
const TenantAppRunnerPage = ({
    navMode = 'master',
    sectionKey,
    sectionTitle,
    sectionItems,
    appUid,
    contentUid,
    allSections = {},
    onSectionClick,
    instanceOverride = null,  // Pre-loaded instance — used by admin preview to bypass API fetch
}) => {
    const { token, user } = useAuth();
    const { formatDate, formatRelativeTime } = useTimezoneFormatter();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const targetRunId = searchParams.get('runId');
    const { addNotification } = useNotification();

    // ── Nav stack (goes up one level on Back) ──────────────────────────────
    // level: 'master' → 'section' → 'app' → 'content'
    const [level, setLevel] = useState(() => {
        if (instanceOverride) return 'app'; // Pre-loaded — jump straight to app gallery
        if (appUid) return 'app';
        if (sectionKey) return 'section';
        return 'master';
    });
    const [currentSection, setCurrentSection] = useState(sectionKey || null);
    const [currentSectionItems, setCurrentSectionItems] = useState(sectionItems || []);
    const [instance, setInstance] = useState(instanceOverride || null); // Seed from override if provided
    const [instanceLoading, setInstanceLoading] = useState(false);
    const [instanceError, setInstanceError] = useState(null);
    const [selectedContent, setSelectedContent] = useState(null);
    const [consoleOpen, setConsoleOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);
    const [currentRun, setCurrentRun] = useState(null);
    const [runHistory, setRunHistory] = useState([]);
    const [flowSteps, setFlowSteps] = useState([]);
    const [stepStatus, setStepStatus] = useState({});
    const [runError, setRunError] = useState(null);
    // Flow canvas viewer
    const [showCanvas, setShowCanvas] = useState(false);
    const [flowSnapshot, setFlowSnapshot] = useState(null);
    // Phase 2: Data Settings panel (must be top-level, not inside a conditional render)
    const [showDataPanel, setShowDataPanel] = useState(false);
    const [dataConfigFlowId, setDataConfigFlowId] = useState(null); // Per-card focused config
    // Full-screen flow canvas viewer (View Flow button)
    const [showFlowEditor, setShowFlowEditor] = useState(false);
    // Prevents auto-opening single content after Back is pressed (bugs #2 & #5)
    const [skipAutoOpen, setSkipAutoOpen] = useState(false);

    // ── Dashboard Refresh (split-button) ─────────────────────────────────
    const [dashRefreshKey, setDashRefreshKey] = useState(0);   // bumped to reload widgets
    const [isDashRefreshing, setIsDashRefreshing] = useState(false);
    const [isDashDataPullRefreshing, setIsDashDataPullRefreshing] = useState(false);
    const [isBackendRefreshing, setIsBackendRefreshing] = useState(false);

    useEffect(() => {
        const startHandler = () => setIsBackendRefreshing(true);
        const stopHandler = () => setIsBackendRefreshing(false);
        window.addEventListener('datamodel-refresh-started', startHandler);
        window.addEventListener('datamodel-refresh-running', startHandler);
        window.addEventListener('datamodel-refresh-completed', stopHandler);
        return () => {
            window.removeEventListener('datamodel-refresh-started', startHandler);
            window.removeEventListener('datamodel-refresh-running', startHandler);
            window.removeEventListener('datamodel-refresh-completed', stopHandler);
        };
    }, []);

    const isDashEffectivelyPullingData = isDashDataPullRefreshing || isBackendRefreshing;

    // Fast: reload widget queries from existing cached parquet (free)
    const handleDashRefresh = useCallback(() => {
        if (isDashRefreshing) return;
        setIsDashRefreshing(true);
        setDashRefreshKey(k => k + 1);
        // isRefreshing resets inside DashboardViewPage; mirror it here after a tick
        setTimeout(() => setIsDashRefreshing(false), 1500);
    }, [isDashRefreshing]);

    // Slow: trigger backend data pull, poll until done, then reload widgets
    const handleDashDataPull = useCallback(async (e) => {
        if (isDashDataPullRefreshing || isBackendRefreshing || isDashRefreshing) return;
        // Resolve data model id from the selected content's dashboard metadata
        // We'll pull it from the DashboardViewPage-compatible field via the instance
        const dashId = selectedContent?.dashboard_id || selectedContent?.id;
        if (!dashId || !token) { handleDashRefresh(); return; }

        addNotification("🔄 Data model refresh initiated. Your dashboard will show updated data once complete — this may take a few minutes.", 'success', 5000);
        setIsDashDataPullRefreshing(true);
        try {
            // Fetch dashboard metadata to get the dataModelId
            const dash = await apiFetch(`/dashboards/${dashId}`, token).catch(() => null);
            const modelId = dash?.theme_config?.dataModelId || dash?.config?.dataModelId;
            if (modelId) {
                let isTenantOverride = false;
                try {
                    // Check if the tenant has configured a data connection override for this platform model
                    const overrideStatus = await apiFetch(`/tenant-portal/data-connect/overrides/${modelId}`, token).catch(() => null);
                    // If the override has a connection configured, it's a tenant override (even if PENDING)
                    if (overrideStatus && overrideStatus.connection_id) {
                        isTenantOverride = true;
                        await apiFetch(`/tenant-portal/data-connect/overrides/${modelId}/sync`, token, { method: 'POST' });
                    }
                } catch (e) { console.warn('[AppRunner] Override check failed:', e?.message); }

                if (!isTenantOverride) {
                    try {
                        // Fallback to normal refresh (for directly-owned models)
                        await apiFetch(`/datamodels/${modelId}/refresh`, token, { method: 'POST' });
                    } catch (e) { console.warn('[AppRunner] Data pull trigger failed:', e?.message); }
                }

                // Notify UI immediately
                window.dispatchEvent(new CustomEvent('datamodel-refresh-started', { detail: { modelId } }));

                const POLL_MS = 3000, MAX_MS = 5 * 60 * 1000;
                const t0 = Date.now();
                let done = false;
                while (!done && Date.now() - t0 < MAX_MS) {
                    await new Promise(r => setTimeout(r, POLL_MS));
                    try {
                        if (isTenantOverride) {
                            const statusResp = await apiFetch(`/tenant-portal/data-connect/overrides/${modelId}`, token).catch(() => null);
                            if (statusResp) {
                                const s = statusResp.data_status;
                                if (s === 'READY' || s === 'FAILED') done = true;
                            }
                        } else {
                            const st = await apiFetch(`/datamodels/${modelId}/refresh-status`, token);
                            const s = (st?.refresh_status || '').toUpperCase();
                            if (s !== 'RUNNING' && s !== 'QUEUED' && s !== 'PENDING') done = true;
                        }
                    } catch { done = true; }
                }
            }
        } finally {
            setIsDashDataPullRefreshing(false);
            setIsBackendRefreshing(false);
        }
        // Reload widget queries against freshly-written parquet
        setIsDashRefreshing(true);
        setDashRefreshKey(k => k + 1);
        setTimeout(() => setIsDashRefreshing(false), 1500);
    }, [isDashDataPullRefreshing, isDashRefreshing, selectedContent, token, handleDashRefresh]);


    const { data: instancesData } = useData(appUid ? '/tenant-portal/apps' : null, token);

    // ── Sync props → state when parent changes ────────────────────────────
    // IMPORTANT: reset ALL run-related state when the app changes so that
    // a run started in App A doesn't show as "Running" in App B.
    useEffect(() => {
        if (appUid) {
            setLevel('app');

            // Try to resolve the instance immediately from cached/prop data
            // so we never flash "Loading app…" when the data is already available.
            const allItems = [
                ...(allSections?.assigned_apps || []),
                ...(allSections?.dashboards   || []),
                ...(allSections?.projects      || []),
                ...(allSections?.apps          || []),
            ];
            const quickMatch =
                instancesData?.find(i => i.id === appUid || i.app_bundle_id === appUid) ||
                allItems.find(i => i.id === appUid || i.app_bundle_id === appUid);

            // Only clear instance if we can't resolve it synchronously
            if (!quickMatch) setInstance(null);

            setSelectedContent(null);
            setConsoleOpen(false);
            setRunHistory([]);
            setFlowSteps([]);
            setStepStatus({});
            setCurrentRun(null);
            setIsExecuting(false);   // ← reset run state on app switch
            setRunError(null);
            setSkipAutoOpen(false);  // ← reset auto-open skip when navigating from sidebar
        } else if (sectionKey) {
            setLevel('section');
            setCurrentSection(sectionKey);
            setCurrentSectionItems(sectionItems || []);
        } else {
            setLevel('master');
        }
    }, [appUid, contentUid, sectionKey, sectionItems, location.key]);

    // ── Load app instance ─────────────────────────────────────────────────
    useEffect(() => {
        // If an override was injected (e.g. admin preview), skip all API calls
        if (instanceOverride) {
            setInstance(instanceOverride);
            setLevel('app');
            setInstanceLoading(false);
            return;
        }
        if (!appUid || level !== 'app') return;
        setInstanceError(null);
        let fromList = instancesData?.find(i => i.id === appUid || i.app_bundle_id === appUid);
        if (!fromList && allSections) {
             const allItems = [...(allSections.assigned_apps || []), ...(allSections.dashboards || []), ...(allSections.projects || []), ...(allSections.apps || [])];
             fromList = allItems.find(i => i.id === appUid || i.app_bundle_id === appUid);
        }
        if (fromList) {
            setInstance(fromList);
            setInstanceLoading(false);
            return;
        }
        // Fallback: fetch from API (only when not available from any local source)
        setInstanceLoading(true);
        apiFetch(`/tenant-portal/apps/${appUid}/metadata`, token)
            .then(d => { setInstance(d); setInstanceLoading(false); })
            .catch(() => { setInstanceError('App not found or access denied.'); setInstanceLoading(false); });
    }, [appUid, instancesData, token, level, instanceOverride]);

    // ── Deep-link to specific content ─────────────────────────────────────
    useEffect(() => {
        if (!instance || !contentUid || skipAutoOpen) return;
        const found = (instance.contents || []).find(c => c.id === contentUid || c.content_uid === contentUid);
        if (found) openContent(found);
    }, [instance, contentUid, skipAutoOpen]);

    // ── Polling ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isExecuting || !currentRun) return;
        const interval = setInterval(async () => {
            try {
                const run = await apiFetch(`/tenant-portal/runs/${currentRun.id}`, token);
                if (run.steps) {
                    const map = {};
                    run.steps.forEach(s => {
                        const start = s.start_time ? dayjs.utc(s.start_time).valueOf() : null;
                        const end = s.end_time ? dayjs.utc(s.end_time).valueOf() : null;
                        map[s.node_id] = {
                            status: s.status,
                            rows_affected: s.rows_affected ?? s.rows,
                            start_time: start,
                            duration: (start && end) ? end - start : null,
                            error_message: s.error_message,
                            metadata: s.metadata_json
                        };
                    });
                    setStepStatus(map);
                }

                // Update the Run History table dynamically while polling
                setRunHistory(prev => prev.map(r => r.id === run.id ? {
                    ...r,
                    status: run.status,
                    completed_at: run.completed_at,
                    rows_affected: run.rows_affected,
                    error_message: run.error_message
                } : r));

                if (['COMPLETED', 'FAILED', 'CANCELLED', 'SUCCESS', 'ERROR'].includes((run.status || '').toUpperCase())) {
                    setIsExecuting(false);
                    if (selectedContent) fetchRunHistory(selectedContent);
                }
            } catch { /* ignore poll errors */ }
        }, 2000);
        return () => clearInterval(interval);
    }, [isExecuting, currentRun, token, selectedContent]);

    const autoDownloadedUrls = useRef(new Set());
    useEffect(() => {
        Object.values(stepStatus).forEach(step => {
            if (['completed', 'success', 'done'].includes((step.status || '').toLowerCase()) && step.metadata?.download_url) {
                const dlUrl = step.metadata.download_url;
                if (!autoDownloadedUrls.current.has(dlUrl)) {
                    autoDownloadedUrls.current.add(dlUrl);
                    const _apiBase = import.meta.env?.VITE_API_BASE_URL?.replace(/\/+$/, '') || '';
                    const apiUrl = dlUrl.startsWith('http') ? dlUrl : `${_apiBase}${dlUrl}`;
                    
                    fetch(apiUrl, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }).then(res => {
                        if (!res.ok) throw new Error('Download failed');
                        return res.blob();
                    }).then(async blob => {
                        const fileName = step.metadata.export_path?.split(/[\/\\]/).pop() || 'download';
                        let directWriteSuccess = false;

                        try {
                            const { getHandle, getAnyHandle } = await import('../utils/localFileStore');
                            const handleKey = step.node_id || step.nodeId || 'file_output';
                            
                            let dirHandle = null;
                            if (window._activeHandles && window._activeHandles[handleKey]) {
                                dirHandle = window._activeHandles[handleKey];
                            }
                            if (!dirHandle) dirHandle = await getHandle(handleKey);
                            
                            if (!dirHandle && window._activeHandles) {
                                const keys = Object.keys(window._activeHandles);
                                if (keys.length > 0) dirHandle = window._activeHandles[keys[0]];
                            }
                            if (!dirHandle) dirHandle = await getAnyHandle();

                            if (dirHandle) {
                                if (dirHandle.queryPermission && dirHandle.requestPermission) {
                                    const perm = await dirHandle.queryPermission({ mode: 'readwrite' });
                                    if (perm !== 'granted') await dirHandle.requestPermission({ mode: 'readwrite' });
                                }
                                const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
                                const writable = await fileHandle.createWritable();
                                await writable.write(blob);
                                await writable.close();
                                directWriteSuccess = true;
                                console.log('[AppRunner] Direct file write successful to authorized directory!');
                            }
                        } catch (e) {
                            console.warn('[AppRunner] Direct file write failed, falling back to browser download:', e);
                        }

                        if (!directWriteSuccess) {
                            const blobUrl = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.download = fileName;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                            console.log('[AppRunner] Auto-download triggered (auth fallback):', apiUrl);
                        }
                    }).catch(err => {
                        console.error('[AppRunner] Auto-download error:', err);
                        addNotification('Auto-download failed. You may need to download manually.', 'error');
                    });
                }
            }
        });
    }, [stepStatus]);

    const fetchFlowStructure = useCallback(async (contentToLoad) => {
        if (!contentToLoad) return;
        try {
            const cid = contentToLoad.content_uid || contentToLoad.id;
            const structure = await apiFetch(`/tenant-portal/apps/${cid}/structure`, token);
            if (structure?.nodes) {
                // Persist raw snapshot for the canvas viewer
                setFlowSnapshot({ nodes: structure.nodes, edges: structure.edges || [] });

                const rawNodes = structure.nodes.filter(n => !['ANNOTATION', 'STICKY_NOTE', 'GROUP', 'NOTE'].includes((n.type || '').toUpperCase()));
                const edges = structure.edges || [];

                const inDegree = {};
                const adj = {};
                rawNodes.forEach(n => { inDegree[n.id] = 0; adj[n.id] = []; });

                edges.forEach(e => {
                    if (inDegree[e.target] !== undefined && inDegree[e.source] !== undefined) {
                        adj[e.source].push(e.target);
                        inDegree[e.target]++;
                    }
                });

                const sortedNodes = [];
                const queue = rawNodes.filter(n => inDegree[n.id] === 0).map(n => n.id);

                const ACTION_NODES = ['EMAIL_ACTION', 'API_CALL', 'TEAMS_NOTIFY', 'GENERATE_DOCUMENT', 'USER_ACTION', 'APPROVAL', 'FETCH_DOCUMENT', 'EDI_PARSE', 'FLOW_RUNNER', 'FOR_EACH', 'BARRIER'];

                const getNodeWeight = (n) => {
                    const type = (n.type || '').toUpperCase();
                    if (ACTION_NODES.includes(type)) return 2;
                    if (type.includes('AI_') || type.includes('ML_') || type === 'LLM_NODE') return 1;
                    return 0; // SQL/Data nodes go first
                };

                while (queue.length > 0) {
                    // Tie-breaker: sort the queue so we process nodes with the lowest weight first
                    queue.sort((aId, bId) => {
                        const aNode = rawNodes.find(n => n.id === aId);
                        const bNode = rawNodes.find(n => n.id === bId);
                        if (!aNode || !bNode) return 0;
                        return getNodeWeight(aNode) - getNodeWeight(bNode);
                    });

                    const currentId = queue.shift();
                    const node = rawNodes.find(n => n.id === currentId);
                    if (node) sortedNodes.push(node);

                    (adj[currentId] || []).forEach(neighborId => {
                        inDegree[neighborId]--;
                        if (inDegree[neighborId] === 0) {
                            queue.push(neighborId);
                        }
                    });
                }

                // Append any remaining nodes (e.g. disconnected or cycles)
                rawNodes.forEach(n => {
                    if (!sortedNodes.find(sn => sn.id === n.id)) {
                        sortedNodes.push(n);
                    }
                });

                setFlowSteps(sortedNodes.map(n => ({ id: n.id, name: n.data?.label || n.name || n.id, type: n.type })));
                return structure;
            }
            return null;
        } catch { /* lazy */ }
    }, [token]);

    const fetchRunHistory = useCallback(async (content, opts = {}) => {
        if (!instance || !content) return;
        try {
            const cid = content.content_uid || content.id;
            const runs = await apiFetch(`/tenant-portal/instances/${instance.id}/runs?contentId=${cid}`, token);
            if (!Array.isArray(runs)) return;
            const sorted = (runs || []).sort((a, b) => dayjs(b.started_at).diff(dayjs(a.started_at)));
            setRunHistory(sorted);

            // ── Background sweep: auto-correct all stale Pending/Running rows ──
            // The detail endpoint cross-references flow_executions and auto-heals the DB row.
            const staleRows = sorted.filter(r => ['pending', 'running', 'Pending', 'Running', 'waiting', 'Waiting'].includes(r.status));
            if (staleRows.length > 0) {
                Promise.all(staleRows.map(r =>
                    apiFetch(`/tenant-portal/runs/${r.id}`, token)
                        .then(detail => ({ id: r.id, status: detail.status, completed_at: detail.completed_at, rows_affected: detail.rows_affected, error_message: detail.error_message }))
                        .catch(() => null)
                )).then(results => {
                    const updates = results.filter(Boolean);
                    if (updates.length > 0) {
                        setRunHistory(prev => {
                            const newHistory = [...prev];
                            let changed = false;

                            updates.forEach(upd => {
                                const idx = newHistory.findIndex(r => r.id === upd.id);
                                if (idx !== -1) {
                                    // only trigger re-render if status actually changed
                                    if (newHistory[idx].status !== upd.status) {
                                        newHistory[idx] = { ...newHistory[idx], status: upd.status, completed_at: upd.completed_at, rows_affected: upd.rows_affected, error_message: upd.error_message };
                                        changed = true;
                                    }
                                }
                            });

                            return changed ? newHistory : prev;
                        });
                    }
                });
            }

            if (opts.hydrate) {
                // Attempt to restore live state from the most recent run or a specific targetRunId
                const targetRun = targetRunId ? sorted.find(r => r.id === targetRunId) : sorted[0];
                if (!targetRun) return;
                const statusUp = (targetRun.status || '').toUpperCase();
                const isNonTerminal = ['RUNNING', 'PENDING', 'WAITING'].includes(statusUp);
                if (isNonTerminal || targetRunId) {
                    // Fetch the detailed run to get its actual status (cross-references flow_executions)
                    try {
                        const detail = await apiFetch(`/tenant-portal/runs/${targetRun.id}`, token);
                        const detailStatus = (detail.status || '').toUpperCase();
                        if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(detailStatus) && !targetRunId) {
                            // Server auto-corrected the status — refresh history
                            setRunHistory(prev => prev.map(r =>
                                r.id === targetRun.id ? { ...r, status: detail.status } : r
                            ));
                            return; // No need to restore as executing unless explicitly requested via URL
                        }
                        // Open the console for the run
                        setCurrentRun(targetRun);
                        if (['RUNNING', 'WAITING', 'PENDING'].includes(detailStatus)) {
                            setIsExecuting(true);
                        } else {
                            setIsExecuting(false);
                        }
                        setConsoleOpen(true);
                        if (detail.steps) {
                            const map = {};
                            detail.steps.forEach(s => {
                                const start = s.start_time ? dayjs.utc(s.start_time).valueOf() : null;
                                const end = s.end_time ? dayjs.utc(s.end_time).valueOf() : null;
                                map[s.node_id] = {
                                    status: s.status,
                                    rows_affected: s.rows_affected ?? s.rows,
                                    start_time: start,
                                    duration: (start && end) ? end - start : null,
                                    error_message: s.error_message
                                };
                            });
                            setStepStatus(map);
                        }
                    } catch { /* ignore */ }
                }
            }
        } catch { /* ignore */ }
    }, [instance, token]);

    const handleViewRun = useCallback(async (run) => {
        if (!run) return;
        try {
            // Optimistically open console and clear old steps
            setStepStatus({});
            setCurrentRun(run);
            setConsoleOpen(true);
            setIsExecuting(['RUNNING', 'WAITING', 'PENDING'].includes((run.status || '').toUpperCase()));

            // Fetch detailed run info
            const detail = await apiFetch(`/tenant-portal/runs/${run.id}`, token);
            if (detail?.steps) {
                const map = {};
                detail.steps.forEach(s => {
                    const start = s.start_time ? dayjs(s.start_time).valueOf() : null;
                    const end = s.end_time ? dayjs(s.end_time).valueOf() : null;
                    map[s.node_id] = {
                        status: s.status,
                        rows_affected: s.rows_affected ?? s.rows,
                        start_time: start,
                        duration: (start && end) ? end - start : null,
                        error_message: s.error_message
                    };
                });
                setStepStatus(map);
            }
        } catch { /* ignore */ }
    }, [token]);

    const openContent = useCallback((content) => {
        // Reset execution state only — keep flow steps fetched lazily
        setIsExecuting(false);
        setCurrentRun(null);
        setRunError(null);
        setStepStatus({});
        setSelectedContent(content);
        setLevel('content');
        setRunHistory([]);
        setFlowSteps([]);
        setFlowSnapshot(null);
        setShowCanvas(false);
        const isFlow = content.type !== 'dashboard' && content.content_type !== 'dashboard';
        // Don't auto-open the console: user should open it intentionally.
        // It will be auto-opened only if a run is already in progress (handled by fetchRunHistory hydrate).
        setConsoleOpen(false);
        if (isFlow) {
            // Wait for instance state to settle, then fetch structure
            setTimeout(() => fetchFlowStructure(content), 0);

            // Pass hydrate:true so fetchRunHistory will attempt to restore in-progress state
            setTimeout(() => fetchRunHistory(content, { hydrate: true }), 0);
        }
    }, [fetchFlowStructure, fetchRunHistory]);

    const handleRun = useCallback(async () => {
        if (!instance || !selectedContent || isExecuting) return;

        let activeStructure = flowSnapshot;

        // Ensure flow structure is loaded before running
        if (flowSteps.length === 0 && selectedContent.type !== 'dashboard' && selectedContent.content_type !== 'dashboard') {
            activeStructure = await fetchFlowStructure(selectedContent);
        }

        // --- Auto-Upload Local Files before execution ---
        let node_overrides = {};
        if (activeStructure?.nodes) {
            for (const node of activeStructure.nodes) {
                const nodeConfig = node.config || node.data || {};
                const nodeType = node.type || nodeConfig.type;
                if (!((nodeType === 'fileSource' || nodeType === 'FILE_SOURCE' || nodeType === 'file_source' || nodeConfig.connectionMode === 'file') && nodeConfig.loadAtEveryRun && nodeConfig.localFilePath)) continue;

                const { resolveFileInDirectory, saveHandle } = await import('../utils/localFileStore');
                const fileName = nodeConfig.localFilePath;
                const dirKey = `dir-${node.id}`;
                let file = await resolveFileInDirectory(dirKey, fileName);
                
                if (!file) {
                    addNotification(`[Auto-Upload] Could not find '${fileName}'. Please authorize the folder containing this file...`, 'info');
                    try {
                        // @ts-ignore
                        const dirHandle = await window.showDirectoryPicker({ id: 'flow-workspace', mode: 'read', startIn: 'downloads' });
                        await saveHandle(dirKey, dirHandle);
                        file = await resolveFileInDirectory(dirKey, fileName);
                        if (!file) throw new Error(`The file '${fileName}' was not found in the selected folder.`);
                    } catch (err) {
                        setRunError(`Could not access folder: ${err.message}`);
                        return;
                    }
                }
                
                addNotification(`[Auto-Upload] Uploading fresh '${file.name}'...`, 'info');
                
                try {
                    const form = new FormData();
                    form.append('file', file);
                    const result = await apiFetch('/flows/ops/upload', token, { method: 'POST', body: form });
                    
                    if (!result || !result.filePath) throw new Error('Upload failed: missing filePath in response');
                    
                    const s3Uri = result.filePath;
                    addNotification(`[Auto-Upload] Uploaded successfully`, 'success');
                    
                    node_overrides[node.id] = {
                        s3Path: s3Uri,
                        autoUploadPath: s3Uri,
                        connectionMode: 'file',
                        sourceType: 'file',
                        filePath: s3Uri
                    };
                } catch (e) {
                    setRunError(`Auto-upload failed for '${file.name}': ${e.message}`);
                    return;
                }
            }
        }
        // ------------------------------------------------

        setIsExecuting(true);
        setRunError(null);
        setStepStatus({});
        try {
            const run = await apiFetch(`/tenant-portal/apps/${instance.id}/run`, token, {
                method: 'POST',
                body: JSON.stringify({
                    content_id: selectedContent.content_uid || selectedContent.id,
                    mode: 'production',  // Always production run from the App Runner
                    node_overrides: Object.keys(node_overrides).length > 0 ? node_overrides : undefined
                })
            });
            setCurrentRun(run);
            setRunHistory(prev => [run, ...prev]);
        } catch (e) {
            setRunError(e.message || 'Execution failed.');
            setIsExecuting(false);
        }
    }, [instance, selectedContent, isExecuting, token]);

    const handleApprove = useCallback(async (nodeId, action) => {
        if (!currentRun) return;
        try {
            await apiFetch(`/tenant-portal/runs/${currentRun.id}/approve`, token, {
                method: 'POST',
                body: JSON.stringify({ node_id: nodeId, action })
            });
        } catch { /* ignore */ }
    }, [currentRun, token]);

    const goBack = useCallback(() => {
        if (level === 'content') {
            // Leaving content runner — stop any in-progress run display
            setIsExecuting(false);
            setCurrentRun(null);
            setSelectedContent(null);
            setConsoleOpen(false);
            setSkipAutoOpen(true); // Prevent single-content auto-open from re-entering content level
            setLevel('app');
        } else if (level === 'app') {
            setInstance(null);
            setSkipAutoOpen(false); // Reset when leaving app level
            if (currentSection) setLevel('section');
            else setLevel('master');
        } else if (level === 'section') {
            setLevel('master');
            setCurrentSection(null);
        }
    }, [level, currentSection]);

    // ── LOADING / ERROR ───────────────────────────────────────────────────
    if (instanceLoading) return (
        <div className="h-full flex flex-col items-center justify-center gap-3 bg-gray-50 dark:bg-gray-950">
            <LoadingSpinner />
            <span className="text-sm text-gray-400">Loading…</span>
        </div>
    );

    if (instanceError) return (
        <div className="h-full flex flex-col items-center justify-center gap-4 bg-gray-50 dark:bg-gray-950 px-8">
            <AlertCircle className="w-12 h-12 text-red-300" />
            <div className="text-center">
                <h2 className="font-bold text-gray-900 dark:text-gray-100">Not Found</h2>
                <p className="text-sm text-gray-400 mt-1">{instanceError}</p>
            </div>
            <button onClick={() => { setInstanceError(null); setLevel('master'); }}
                className="text-sm text-indigo-600 hover:underline">← My Apps</button>
        </div>
    );

    // ══════════════════════════════════════════════════════════════════════════
    //  LEVEL 1 — MASTER VIEW (4 section cards)
    // ══════════════════════════════════════════════════════════════════════════
    if (level === 'master') {
        const sections = [
            { key: 'assigned_apps', items: allSections.assigned_apps || [] },
            { key: 'dashboards', items: allSections.dashboards || [] },
            { key: 'projects', items: allSections.projects || [] },
            { key: 'apps', items: allSections.apps || [] },
        ].filter(s => s.items.length > 0);

        const gradients = {
            assigned_apps: 'from-violet-500 to-purple-600',
            dashboards: 'from-blue-500 to-cyan-500',
            projects: 'from-emerald-500 to-teal-600',
            apps: 'from-indigo-500 to-violet-600',
        };

        const totalItems = sections.reduce((a, s) => a + s.items.length, 0);

        return (
            <div className="h-full flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
                {/* Animated page banner */}
                <PageBanner
                    icon={<Layers className="w-5 h-5" />}
                    title="My Apps"
                    subtitle={`${totalItems} item${totalItems !== 1 ? 's' : ''} across ${sections.length} section${sections.length !== 1 ? 's' : ''}`}
                    accentGradient="from-indigo-500 to-violet-600"
                />

                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {sections.length === 0 ? (
                        <div className="py-24 text-center">
                            <Layers className="w-12 h-12 text-gray-200 dark:text-gray-700 mx-auto mb-3" />
                            <p className="text-gray-400">No apps published yet</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                            {sections.map(({ key, items }) => {
                                const meta = sectionMeta[key];
                                const Icon = meta.icon;
                                const grad = gradients[key];
                                // Build sub-counts (flows vs dashboards)
                                const dashCount = items.filter(i => i.type === 'dashboard' || i.content_type === 'dashboard').length;
                                const flowCount = items.length - dashCount;

                                return (
                                    <button key={key}
                                        onClick={() => {
                                            setCurrentSection(key);
                                            setCurrentSectionItems(items);
                                            setLevel('section');
                                        }}
                                        className="group text-left flex flex-col rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-200 active:scale-[0.98] focus:outline-none">

                                        {/* Gradient accent top */}
                                        <div className={`h-24 bg-gradient-to-br ${grad} flex items-center justify-center relative overflow-hidden`}>
                                            <div className="absolute inset-0 opacity-20"
                                                style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, white 0%, transparent 60%)' }} />
                                            <Icon className="w-10 h-10 text-white drop-shadow-md" />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 p-4 flex flex-col gap-2">
                                            <div className="flex items-start justify-between">
                                                <h3 className="font-bold text-gray-900 dark:text-gray-100 text-base leading-tight">{meta.label}</h3>
                                                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors shrink-0 mt-0.5" />
                                            </div>
                                            <p className="text-xs text-gray-400 leading-relaxed">{meta.desc}</p>

                                            {/* Item count breakdown */}
                                            <div className="mt-auto pt-3 border-t border-gray-50 dark:border-gray-800 flex items-center justify-between">
                                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                                    {items.length} item{items.length !== 1 ? 's' : ''}
                                                </span>
                                                {(dashCount > 0 || flowCount > 0) && key !== 'dashboards' && (
                                                    <span className="text-[10px] text-gray-300 dark:text-gray-600">
                                                        {flowCount > 0 && `${flowCount} flow${flowCount > 1 ? 's' : ''}`}
                                                        {flowCount > 0 && dashCount > 0 && ' · '}
                                                        {dashCount > 0 && `${dashCount} dashboard${dashCount > 1 ? 's' : ''}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ══════════════════════════════════════════════════════════════════════════
    //  LEVEL 2 — SECTION GALLERY
    // ══════════════════════════════════════════════════════════════════════════
    if (level === 'section') {
        const meta = sectionMeta[currentSection] || sectionMeta.apps;
        const Icon = meta.icon;
        const sectionItems = currentSectionItems;
        const isDashSection = currentSection === 'dashboards';

        return (
            <div className="h-full flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
                {/* Animated section banner */}
                <PageBanner
                    icon={<Icon className="w-5 h-5" />}
                    title={meta.label}
                    subtitle={meta.desc}
                    accentGradient={meta.gradient || 'from-indigo-500 to-violet-600'}
                    actions={
                        <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full font-medium">
                            {sectionItems.length} item{sectionItems.length !== 1 ? 's' : ''}
                        </span>
                    }
                />
                {/* Back nav */}
                <div className="shrink-0 px-6 pt-3">
                    <button onClick={() => setLevel('master')}
                        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary transition-colors">
                        <ChevronLeft className="w-3.5 h-3.5" /> My Apps
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-8 py-6">
                    {sectionItems.length === 0 ? (
                        <div className="py-24 text-center">
                            <Icon className="w-12 h-12 text-gray-200 dark:text-gray-700 mx-auto mb-3" />
                            <p className="text-gray-400">No {meta.label.toLowerCase()} yet</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {sectionItems.map(item => (
                                <ItemCard
                                    key={item.id}
                                    item={{ ...item, type: isDashSection ? 'dashboard' : item.type }}
                                    sectionKey={currentSection}
                                    formatDate={formatDate}
                                    formatRelativeTime={formatRelativeTime}
                                    onClick={() => {
                                        if (isDashSection) {
                                            // Dashboard directly opens view, no app-level gallery needed
                                            setInstance({
                                                id: item.id, name: item.name, description: item.description,
                                                contents: [{ id: item.id, name: item.name, type: 'dashboard', dashboard_id: item.dashboard_id || item.id }]
                                            });
                                            setLevel('app');
                                        } else if (onSectionClick) {
                                            onSectionClick(item);
                                        } else {
                                            // Load app
                                            setInstance(null);
                                            setLevel('app');
                                            // appUid triggers the useEffect above to fetch
                                            // But since we're in standalone mode, fetch directly
                                            setInstanceLoading(true);
                                            apiFetch(`/tenant-portal/apps/${item.id}/metadata`, token)
                                                .then(d => { setInstance(d); setInstanceLoading(false); })
                                                .catch(() => { setInstanceError('App not found.'); setInstanceLoading(false); });
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Wait for instance at app level ────────────────────────────────────
    if ((level === 'app' || level === 'content') && !instance) {
        return (
            <div className="h-full flex flex-col items-center justify-center gap-3 bg-gray-50 dark:bg-gray-950">
                <LoadingSpinner />
                <span className="text-sm text-gray-400">Loading app…</span>
            </div>
        );
    }

    const contents = instance?.contents || [];
    // A content item is a "dashboard" if it is:
    //   - an AppDashboard record  (type === 'dashboard'), OR
    //   - a Content record published as a dashboard  (content_type === 'dashboard')
    const isDashContent = (c) => c.type === 'dashboard' || c.content_type === 'dashboard';
    const dashboards = contents.filter(isDashContent);
    const flows = contents.filter(c => !isDashContent(c));
    const sectionMeta_ = sectionMeta[currentSection] || sectionMeta.apps;

    // ══════════════════════════════════════════════════════════════════════════
    //  LEVEL 3 — APP GALLERY (flows + dashboards within one app)
    // ══════════════════════════════════════════════════════════════════════════
    if (level === 'app') {
        // ── Deep-link: content already selected (e.g. sidebar sub-item click) ──
        // selectedContent is set by the useEffect[instance, contentUid] → return the runner directly.
        // If contentUid is provided but instance not yet loaded, show only a spinner (no gallery flash).
        if (contentUid && !selectedContent && !skipAutoOpen) {
            return (
                <div className="h-full flex items-center justify-center">
                    <LoadingSpinner />
                </div>
            );
        }

        // If single content and it's NOT an App Bundle, skip gallery and go straight to runner
        if (contents.length === 1 && !selectedContent && !skipAutoOpen && instance?.type !== 'App Bundle') {
            openContent(contents[0]);
            return (
                <div className="h-full flex items-center justify-center">
                    <LoadingSpinner />
                </div>
            );
        }


        // Phase 2: derived constants (not hooks — safe inside conditional render)
        const isAdmin = isPlatformScoped(user) || user?.roleId === 'r4';
        // bundleId must be the app_bundle_id, NOT the instance id
        const bundleId = instance?.app_bundle_id || instance?.appBundleId;

        return (
            <div className="h-full flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
                {/* Full-screen flow canvas viewer */}
                {showFlowEditor && (
                    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-6">
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full h-full max-w-[95vw] max-h-[92vh] overflow-hidden flex flex-col">
                            <FlowEditorPage
                                flowId={selectedContent?.flow_id || selectedContent?.content_uid || selectedContent?.id}
                                onBack={() => setShowFlowEditor(false)}
                                forceReadOnly={true}
                                externalStepStatus={stepStatus}
                                externalIsExecuting={isExecuting}
                                onRun={() => {
                                    setShowFlowEditor(false);
                                    setConsoleOpen(true);
                                    handleRun();
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Phase 2: Data Settings slide-over */}
                {showDataPanel && bundleId && (
                    <ConnectDataPanel
                        bundleId={bundleId}
                        appName={instance.name}
                        onClose={() => { setShowDataPanel(false); setDataConfigFlowId(null); }}
                        initialTab={dataConfigFlowId ? 'flows' : undefined}
                        focusFlowId={dataConfigFlowId}
                    />
                )}

                {/* Custom Header replacing PageHeader for Level 3 per user request */}
                <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        {navMode !== 'app' && (
                            <button
                                onClick={goBack}
                                className="p-2 -ml-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors"
                                title="Back"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                        )}
                        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 truncate max-w-xl">
                            {instance.name}
                        </h1>
                    </div>
                    {/* Phase 2: Data Settings button — visible to admins only */}
                    {isAdmin && bundleId && (
                        <button
                            id="data-settings-btn"
                            onClick={() => setShowDataPanel(true)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300
                                       hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300
                                       border border-slate-200 dark:border-slate-700 rounded-lg transition-colors"
                            title="Connect your organization's data to this app"
                        >
                            <Database className="w-4 h-4" />
                            <span className="hidden sm:inline">Data Settings</span>
                        </button>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
                    {instance.description && (
                        <p className="text-sm text-gray-400">{instance.description}</p>
                    )}
                    {dashboards.length > 0 && (
                        <section>
                            <div className="flex items-center gap-2 mb-4">
                                <BarChart3 className="w-4 h-4 text-blue-400" />
                                <span className="text-xs font-black uppercase tracking-widest text-gray-400">Dashboards</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {dashboards.map(d => <ItemCard key={d.id} item={d} sectionKey="dashboards" formatDate={formatDate} formatRelativeTime={formatRelativeTime} onClick={() => openContent(d)} isAdmin={isAdmin} onConfigData={bundleId ? (item) => { setDataConfigFlowId(null); setShowDataPanel(true); } : null} />)}
                            </div>
                        </section>
                    )}
                    {flows.length > 0 && (
                        <section>
                            <div className="flex items-center gap-2 mb-4">
                                <Zap className="w-4 h-4 text-indigo-400" />
                                <span className="text-xs font-black uppercase tracking-widest text-gray-400">Flows &amp; Analysis</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {flows.map(f => <ItemCard key={f.id} item={f} sectionKey="apps" formatDate={formatDate} formatRelativeTime={formatRelativeTime} onClick={() => openContent(f)} isAdmin={isAdmin} onConfigData={bundleId ? (item) => { setDataConfigFlowId(item.flow_id || item.content_uid || item.id); setShowDataPanel(true); } : null} />)}
                            </div>
                        </section>
                    )}
                    {contents.length === 0 && (
                        <div className="py-24 text-center">
                            <Layers className="w-12 h-12 text-gray-200 dark:text-gray-700 mx-auto mb-3" />
                            <p className="text-gray-400 text-sm">No contents published yet</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ======================================================================
    //  LEVEL 4 â€” CONTENT RUNNER
    // ======================================================================
    const isDashboard = selectedContent?.type === 'dashboard' || selectedContent?.content_type === 'dashboard';
    const lastRun = runHistory[0];
    const successRate = runHistory.length > 0
        ? Math.round((runHistory.filter(r => ['Completed', 'Success', 'COMPLETED', 'SUCCESS'].includes(r.status)).length / runHistory.length) * 100)
        : null;

    return (
        <div className={`flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950 ${isFullScreen ? 'fixed inset-0 z-[9990]' : 'relative h-full'}`}>

            {showFlowEditor && (
                <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-6">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full h-full max-w-[95vw] max-h-[92vh] overflow-hidden flex flex-col">
                        <FlowEditorPage
                            flowId={selectedContent?.flow_id || selectedContent?.content_uid || selectedContent?.id}
                            onBack={() => setShowFlowEditor(false)}
                            forceReadOnly={true}
                            externalStepStatus={stepStatus}
                            externalIsExecuting={isExecuting}
                            onRun={() => {
                                setShowFlowEditor(false);
                                setConsoleOpen(true);
                                handleRun();
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                    <button
                        onClick={goBack}
                        className="p-2 -ml-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors"
                        title="Back"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 truncate max-w-xl">
                            {selectedContent.name}
                        </h1>
                    </div>
                </div>

                {/* Right Actions */}
                <div className="flex items-center gap-2">
                    {/* Split Refresh â€” shown only for dashboard content */}
                    {isDashboard && (
                        <div className="flex items-center h-9 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                            <button
                                onClick={handleDashRefresh}
                                disabled={isDashRefreshing}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border-r border-slate-200 dark:border-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={isDashRefreshing ? 'Refreshing...' : 'Reload from cached data'}
                            >
                                <RefreshCw className={`w-4 h-4 ${isDashRefreshing ? 'animate-spin' : ''}`} />
                                <span className="hidden sm:inline">{isDashRefreshing ? 'Refreshing...' : 'Refresh'}</span>
                            </button>
                            <RunnerDropdownMenu>
                                <RunnerDropdownMenuTrigger asChild>
                                    <button
                                        disabled={isDashRefreshing || isDashEffectivelyPullingData}
                                        className="flex items-center justify-center px-2 py-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-full"
                                        title="More refresh options"
                                    >
                                        <ChevronDown className={`w-3.5 h-3.5 ${isDashEffectivelyPullingData ? 'text-amber-500 animate-pulse' : ''}`} />
                                    </button>
                                </RunnerDropdownMenuTrigger>
                                <RunnerDropdownMenuContent align="end" className="w-56">
                                    <RunnerDropdownMenuItem disabled={isDashEffectivelyPullingData} onSelect={handleDashDataPull}>
                                        <div className="flex flex-col gap-0.5 py-0.5">
                                            <span className="font-medium text-sm">
                                                {isDashEffectivelyPullingData ? 'Refreshing full Data...' : 'Refresh with Data Pull'}
                                            </span>
                                            <span className="text-[10px] text-gray-400">Fetches latest from source DB</span>
                                        </div>
                                    </RunnerDropdownMenuItem>
                                </RunnerDropdownMenuContent>
                            </RunnerDropdownMenu>
                        </div>
                    )}

                    <button
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                    >
                        {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                        <span className="hidden sm:inline">{isFullScreen ? 'Exit Full Screen' : 'Full Screen'}</span>
                    </button>

                    {!isDashboard && (
                        <div className="flex items-center gap-2 border-l border-slate-200 dark:border-slate-700 pl-2 ml-1">
                            {lastRun && !consoleOpen && (
                                <div className="hidden md:flex items-center gap-2 text-sm text-gray-400 mr-1">
                                    <StatusDot status={lastRun.status} />
                                    <span className="text-xs">{fmtRelative(lastRun.started_at, formatDate)}</span>
                                </div>
                            )}
                            <button
                                onClick={() => { setConsoleOpen(v => !v); if (!consoleOpen) fetchFlowStructure(); }}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-all active:scale-95 ${consoleOpen
                                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                                    : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-100 dark:shadow-none'}`}>
                                {consoleOpen ? <><X className="w-3.5 h-3.5" /> Close Console</> : <><Play className="w-3.5 h-3.5" fill="currentColor" /> {'Run'}</>}
                            </button>
                            {!consoleOpen && (
                                <button onClick={() => fetchRunHistory(selectedContent)}
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                                    <RefreshCw className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 flex overflow-hidden relative z-0">

                {/* ── Full-screen Published • View Only canvas ─────────── */}
                {showCanvas && !isDashboard && (
                    <div className="fixed inset-0 z-[9999] bg-white dark:bg-gray-900">
                        <FlowRunViewer
                            flowSnapshot={flowSnapshot}
                            executionId={currentRun?.id}
                            isExecuting={isExecuting}
                            stepStatus={stepStatus}
                            currentRun={currentRun}
                            flowName={selectedContent?.name}
                            onBack={() => setShowCanvas(false)}
                            onClose={() => setShowCanvas(false)}
                        />
                    </div>
                )}
                {/* Main */}
                <div className={`flex-1 min-w-0 ${isDashboard ? 'flex flex-col overflow-hidden' : 'overflow-auto'}`}>
                    {isDashboard ? (
                        // key={dashRefreshKey} unmounts/remounts to force a full widget reload
                        <DashboardViewPage
                            key={dashRefreshKey}
                            dashboardId={selectedContent.dashboard_id || selectedContent.id}
                            embedded
                            readOnly
                            externalFullScreen={isFullScreen}
                            onToggleFullScreen={() => setIsFullScreen(v => !v)}
                        />
                    ) : (
                        <div className="max-w-5xl mx-auto px-8 py-8 space-y-6">
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                {[
                                    { icon: History, label: 'Total Runs', value: runHistory.length },
                                    { icon: TrendingUp, label: 'Success Rate', value: successRate != null ? `${successRate}%` : '—' },
                                    { icon: CalendarClock, label: 'Last Run', value: lastRun ? fmtRelative(lastRun.started_at, formatDate) : '—', small: true },
                                ].map(({ icon: Icon, label, value, small }) => (
                                    <div key={label} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5">
                                        <p className="text-xs text-gray-400 font-medium mb-2 flex items-center gap-1.5">
                                            <Icon className="w-3 h-3" /> {label}
                                        </p>
                                        <p className={`font-black text-gray-900 dark:text-gray-100 ${small ? 'text-base' : 'text-3xl'}`}>{value}</p>
                                    </div>
                                ))}
                            </div>

                            {/* CTA — shown when console closed */}
                            {!consoleOpen && (
                                <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-6 flex items-center gap-4 shadow-xl shadow-primary/10 dark:shadow-none">
                                    <div className="flex-1">
                                        <h3 className="text-white font-bold text-base">{selectedContent.name}</h3>
                                        <p className="text-primary/20 text-sm mt-0.5">{selectedContent.description || 'Ready to run. Open the console to start.'}</p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {!!selectedContent?.dashboard_id && ['Completed', 'Success', 'COMPLETED', 'SUCCESS'].includes(lastRun?.status) && (
                                            <button
                                                onClick={() => openContent({ id: selectedContent.dashboard_id, type: 'dashboard', name: selectedContent.name + ' Results' })}
                                                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-semibold px-4 py-2.5 rounded-xl transition-all border border-white/30"
                                            >
                                                <LayoutDashboard className="w-4 h-4" />
                                                View Results Dashboard
                                            </button>
                                        )}
                                        {/* View Flow — opens FlowEditorPage in Published • View Only mode */}
                                        <button
                                            onClick={() => setShowFlowEditor(true)}
                                            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-semibold px-4 py-2.5 rounded-xl transition-all border border-white/30"
                                            title="Preview the flow canvas"
                                        >
                                            <Eye className="w-4 h-4" />
                                            View Flow
                                        </button>
                                        <button onClick={() => { setConsoleOpen(true); fetchFlowStructure(selectedContent); }}
                                            className="flex items-center gap-2 bg-white text-primary font-bold px-5 py-2.5 rounded-xl shadow-md hover:bg-primary/5 transition-all active:scale-95">
                                            <Play className="w-4 h-4" fill="currentColor" /> Run App
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Run History */}
                            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-50 dark:border-gray-800">
                                    <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                        <Activity className="w-4 h-4 text-gray-400" /> Run History
                                    </h3>
                                    {runHistory.length > 0 && (
                                        <span className="text-xs text-gray-400 bg-gray-50 dark:bg-gray-800 px-2.5 py-1 rounded-full">
                                            {runHistory.length} runs
                                        </span>
                                    )}
                                </div>
                                {runHistory.length > 0
                                    ? <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-100 dark:border-gray-800">
                                                <th className="pl-5 pr-2 py-2.5 w-10" />
                                                <th className="pr-4 py-2.5 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Date / Time</th>
                                                <th className="pr-4 py-2.5 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Duration</th>
                                                <th className="pr-4 py-2.5 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Rows Processed</th>
                                                <th className="pr-2 py-2.5 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Status</th>
                                                <th className="pr-5 py-2.5" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                            {runHistory.slice(0, 10).map(run => (
                                                <RunHistoryRow
                                                    key={run.id}
                                                    run={run}
                                                    isSelected={currentRun?.id === run.id && consoleOpen}
                                                    onClick={() => handleViewRun(run)}
                                                    onViewResults={(r) => handleViewRun(r)}
                                                    formatDate={formatDate}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                    : <div className="py-10 text-center">
                                        <Hash className="w-7 h-7 text-gray-200 dark:text-gray-700 mx-auto mb-2" />
                                        <p className="text-sm text-gray-400">No runs yet</p>
                                    </div>
                                }
                            </div>
                        </div>
                    )}
                </div>



                {/* Execution Console */}
                {consoleOpen && !isDashboard && (
                    <div className="w-[380px] shrink-0 h-full overflow-hidden shadow-[-8px_0_32px_rgba(0,0,0,0.06)]">
                        <ExecutionConsole
                            onClose={() => setConsoleOpen(false)}
                            onRun={handleRun}
                            isExecuting={isExecuting}
                            flowSteps={flowSteps}
                            stepStatus={stepStatus}
                            currentRun={currentRun}
                            error={runError}
                            onApprove={handleApprove}
                            linkedDashboardId={selectedContent?.dashboard_id}
                            onOpenDashboard={(dashId) => openContent({ id: dashId, type: 'dashboard', name: selectedContent.name + ' Results' })}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TenantAppRunnerPage;
